import { useState, type ReactNode } from 'react';
import { Pencil, Plus, Route, Trash2 } from 'lucide-react';
import { Button, Card, ConfirmDialog, EmptyState, IconButton, TableSkeleton } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { RedirectDialog, REDIRECT_CODE_LABELS } from '@/components/sites/dns/RedirectDialog';
import type { ZoneSectionProps } from '@/components/sites/dns/ZoneSections';
import { useRedirectChange, useRedirects } from '@/hooks/useDomains';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import type { RedirectView } from '@/types/api';

const STATUS: Record<string, { label: string; tone: string }> = {
  ACTIVE: { label: 'работает', tone: 'tone-healthy' },
  PENDING: { label: 'применяется', tone: 'muted' },
  SYNC_FAILED: { label: 'не применился', tone: 'tone-down' },
};

/** How a redirect matches and what it carries over, in words. */
function how(redirect: RedirectView): string {
  return [
    redirect.subpathMatching ? 'и все адреса ниже' : null,
    redirect.preservePathSuffix ? 'с остатком пути' : null,
    redirect.preserveQueryString ? 'с параметрами' : null,
    redirect.includeSubdomains ? 'и поддомены' : null,
  ]
    .filter(Boolean)
    .join(', ');
}

/**
 * The domain's Cloudflare Bulk Redirects: answered by Cloudflare before a
 * request reaches the server, so they work whatever the site runs on — as long
 * as the name is proxied through Cloudflare.
 */
export function RedirectsSection({ siteId, zone, may }: ZoneSectionProps): ReactNode {
  const domainId = zone.domainId ?? '';
  const redirects = useRedirects(siteId, domainId, zone.cloudflare && zone.domainId !== null);
  const [editing, setEditing] = useState<RedirectView | 'new' | null>(null);
  const [deleting, setDeleting] = useState<RedirectView | null>(null);
  const remove = useRedirectChange(siteId, domainId, {
    onSuccess: () => {
      toast.success('Редирект удалён');
      setDeleting(null);
    },
  });

  if (!zone.cloudflare) {
    return (
      <Card flush title="Редиректы" icon={Route}>
        <EmptyState icon={Route} message="Домен не обслуживается Cloudflare" />
      </Card>
    );
  }

  return (
    <Card
      flush
      title="Редиректы"
      icon={Route}
      actions={
        may ? (
          <Button size="sm" icon={Plus} onClick={() => setEditing('new')}>
            Добавить редирект
          </Button>
        ) : null
      }
    >
      <p className="field-hint" style={{ margin: 'var(--space-3) var(--space-4) 0' }}>
        Выполняются в Cloudflare для проксируемых имён.
      </p>
      {redirects.isPending ? (
        <TableSkeleton rows={2} columns={4} />
      ) : redirects.isError ? (
        <ErrorState error={redirects.error} fallback="Не удалось прочитать редиректы из 2d2s" />
      ) : redirects.data.length === 0 ? (
        <EmptyState icon={Route} message="Редиректов нет" />
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">Откуда</th>
                <th scope="col">Куда</th>
                <th scope="col">Код</th>
                <th scope="col">Состояние</th>
                {may ? <th scope="col" aria-label="Действия" /> : null}
              </tr>
            </thead>
            <tbody>
              {redirects.data.map((redirect) => {
                const status = STATUS[redirect.status] ?? { label: redirect.status, tone: '' };
                const details = how(redirect);
                return (
                  <tr key={redirect.id}>
                    <td>
                      <div className="stack stack-tight">
                        <span className="mono" style={{ wordBreak: 'break-all' }}>
                          {redirect.sourceUrl}
                        </span>
                        {details ? <span className="muted text-sm">{details}</span> : null}
                      </div>
                    </td>
                    <td className="mono" style={{ wordBreak: 'break-all' }}>
                      {redirect.targetUrl}
                    </td>
                    <td className="nowrap">{REDIRECT_CODE_LABELS[redirect.statusCode] ?? redirect.statusCode}</td>
                    <td>
                      <div className="stack stack-tight">
                        <span className={status.tone}>{status.label}</span>
                        {redirect.error ? <span className="text-sm tone-down">{redirect.error}</span> : null}
                      </div>
                    </td>
                    {may ? (
                      <td className="nowrap">
                        <IconButton icon={Pencil} size="sm" variant="ghost" label="Изменить" onClick={() => setEditing(redirect)} />
                        <IconButton icon={Trash2} size="sm" variant="ghost" label="Удалить" onClick={() => setDeleting(redirect)} />
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing ? <RedirectDialog siteId={siteId} zone={zone} redirect={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}

      <ConfirmDialog
        open={deleting !== null}
        title="Удалить редирект"
        description={deleting ? `${deleting.sourceUrl} → ${deleting.targetUrl}` : undefined}
        confirmLabel="Удалить"
        destructive
        busy={remove.isPending}
        error={remove.isError ? errorText(remove.error) : undefined}
        onConfirm={() => deleting && remove.mutate({ kind: 'delete', redirectId: deleting.id })}
        onClose={() => {
          remove.reset();
          setDeleting(null);
        }}
      >
        <p className="field-hint">Адрес снова будет открываться как есть, без перенаправления.</p>
      </ConfirmDialog>
    </Card>
  );
}
